import React from 'react';

import { TierKey } from '../../constants';

import { OptimizedImage, ImageData } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';
import { Timer } from 'shared/constants/types';

import attention from 'assets/png/!.png?format=webp;avif&as=picture';

import { CoinBadge } from './coin-badge';

type CardProps = {
  front: ImageData;
  back: ImageData;
  count: number;
  canForge: boolean;
  canMint: boolean;
  /** Controlled: true = fully open (front). false = closed (back). */
  open?: boolean;
  openTimeOut?: number | null;

  /** Visual demo only — keep false for mobile friendliness */
  hoverFlip?: boolean;

  /** Peek settings (when closed and user clicks) */
  peekPercent?: number; // 0..1 of the full flip (default 0.25)
  peekDurationMs?: number; // ms for each leg of the animation (default 350)
  peekHoldMs?: number; // ms to hold at peek before closing (default 120)

  move: MovesEnum;
  collection: TierKey;
  onMint: (value: MovesEnum, collection: TierKey) => void;
  onForge: (value: MovesEnum, collection: TierKey) => void;
};

export const Card: React.FC<CardProps> = ({
  front,
  back,
  count,
  canForge,
  canMint,
  open,
  openTimeOut,
  hoverFlip = false,
  peekPercent = 0.25,
  peekDurationMs = 350,
  peekHoldMs = 120,
  move,
  collection,
  onMint,
  onForge,
}) => {
  const [peeking, setPeeking] = React.useState(false);
  const peekTimer = React.useRef<Timer | null>(null);
  const openTimer = React.useRef<Timer | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      openTimer.current = setTimeout(() => setIsOpen(true), openTimeOut);
    }
    if (open === false) {
      setIsOpen(false);
    }
  }, [openTimeOut, open]);

  React.useEffect(
    () => () => {
      if (peekTimer.current) window.clearTimeout(peekTimer.current);
      if (openTimer.current) window.clearTimeout(openTimer.current);
    },
    [],
  );

  const handleClick = () => {
    if (canForge) {
      onForge(move, collection);
      return;
    }
    if (canMint) {
      onMint(move, collection);
      return;
    }
    if (isOpen) return; // when truly open, do nothing (stays open)
    // Closed: run a peek (to N% then back)
    if (peekTimer.current) window.clearTimeout(peekTimer.current);
    setPeeking(true);
    peekTimer.current = setTimeout(() => setPeeking(false), peekDurationMs + peekHoldMs);
  };

  // Target rotation (deg):
  // - open  => 180deg (front visible)
  // - peeking => 180 * peekPercent (slight open)
  // - else => 0deg (back)
  const targetDeg = isOpen ? 180 : peeking ? 180 * Math.max(0, Math.min(1, peekPercent)) : 0;
  return (
    <div className={`inline-flex flex-col items-center justify-center ${hoverFlip ? 'group' : ''}`}>
      <button
        type="button"
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-label="Flip card"
        className={[
          'relative w-full [aspect-ratio:35/52]',
          '[perspective:1000px]',
          'm-0 rounded-lg border-0 bg-transparent p-0',
          'outline-none ring-0 focus:outline-none focus:ring-0',
          'relative [-webkit-tap-highlight-color:transparent]',
        ].join(' ')}
      >
        <div
          className={[
            'relative h-full w-full', // <- fill the aspect box
            '[transform-style:preserve-3d]',
            'will-change-transform',
            'transition-transform ease-out',
            hoverFlip ? 'group-hover:[transform:rotateY(180deg)]' : '',
          ].join(' ')}
          style={{
            transform: `rotateY(${targetDeg}deg)`,
            transitionDuration: `${peekDurationMs}ms`,
          }}
        >
          <div className="absolute inset-0 overflow-hidden rounded-lg [backface-visibility:hidden] [transform:rotateY(180deg)]">
            <OptimizedImage
              imageData={front}
              alt="front"
              draggable={false}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="absolute inset-0 overflow-hidden rounded-lg [backface-visibility:hidden]">
            <OptimizedImage
              imageData={back}
              alt="back"
              draggable={false}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        {count > 0 && (
          <CoinBadge
            className="absolute bottom-[0.5rem] right-[-0.5rem] md:bottom-[1rem] md:right-[-0.5rem]"
            size={24}
            value={count}
          />
        )}
        {(canForge || canMint) && (
          <OptimizedImage
            imageData={attention}
            alt="attention"
            draggable={false}
            className="absolute right-[-0.5rem] top-[0.5rem] w-6 animate-badgePop motion-reduce:animate-none md:right-[-0.5rem] md:top-[1rem]"
          />
        )}
      </button>
    </div>
  );
};
